import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

const SLOW_START_THRESHOLD_MS = 4000;

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slowStart, setSlowStart] = useState(false);

  async function fetchRole() {
    try {
      const { data, error } = await supabase.rpc('get_my_role');
      if (error) throw error;
      setRole(data);
      return data;
    } catch {
      setRole(null);
      return null;
    }
  }

  useEffect(() => {
    const slowStartTimer = setTimeout(() => setSlowStart(true), SLOW_START_THRESHOLD_MS);

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) await fetchRole();
      setLoading(false);
      setSlowStart(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchRole();
      } else {
        setRole(null);
      }
      setLoading(false);
      setSlowStart(false);
    });

    return () => {
      clearTimeout(slowStartTimer);
      listener.subscription.unsubscribe();
    };
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
  }

  const isCoachRole = role && ['superadmin', 'admin_coach', 'coach'].includes(role);
  const isClientRole = role === 'client';

  return (
    <AuthContext.Provider value={{ session, user, role, loading, slowStart, signOut, isCoachRole, isClientRole, fetchRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth moet binnen AuthProvider gebruikt worden');
  return ctx;
}
